import { TasksForEntity } from 'app/agriculture/infobox/tasks/TasksForEntity/TasksForEntity';
import { useNavigate } from 'react-router-dom';
import { IconBaseProps } from 'react-icons';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { AnimalIcon } from 'app/agriculture/icons/components';
import { StatDisplay } from 'app/agriculture/components/StatDisplay/StatDisplay';
import { useHerd } from 'app/agriculture/queries/herds/useHerd';
import { InlineButton } from 'app/agriculture/components/Button/Button';
import { HerdSnapshot } from 'app/agriculture/queries/herds/types/HerdSnapshot';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { InlineAlert } from 'app/agriculture/components/InlineAlert/InlineAlert';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { usePaddock } from 'app/agriculture/queries/entities/usePaddock';
import { HerdDragAndDrop } from 'app/agriculture/hooks/useHerdDragAndDrop/HerdDragAndDrop';
import { useSnapshot } from 'app/agriculture/queries/snapshots/useSnapshot';
import { SuspenseWrapper } from 'app/agriculture/components/SuspenseWrapper/SuspenseWrapper';
import { HerdShowOnMapButton } from 'app/agriculture/routes/Herds/HerdDetails/components/HerdShowOnMapButton';

interface HerdDetailsComponentProps {
    herdId: string;
}

export function HerdDetailsComponent({ herdId }: HerdDetailsComponentProps) {
    const navigate = useNavigate();
    const routeActions = useRouteActions();
    const { name, animalSpecies, herdType } = useHerd({ herdId });
    const snapshot = useSnapshot({ snapshotType: 'Herd', parentId: herdId, shelfLife: null });

    const Icon = (props: IconBaseProps) => (
        <AnimalIcon animalSpecies={animalSpecies} herdType={herdType} {...props} />
    );

    return (
        <ContentBox
            className="HerdDetailsComponent"
            title={name}
            variant="lg"
            Icon={Icon}
            showOnMapButton={
                <SuspenseWrapper loadingComponent={null}>
                    <HerdShowOnMapButton herdId={herdId} />
                </SuspenseWrapper>
            }
        >
            <HerdDragAndDrop />
            <ContentBox.Section scrollable>
                <ContentBox.Section>
                    <InlineAlert type={AgricultureToastType.INFO}>
                        To relocate the herd, drag and drop them into a different paddock
                    </InlineAlert>
                </ContentBox.Section>
                <ContentBox.Section>
                    <SimpleGrid columns={2}>
                        <TasksForEntity assetId={herdId} />
                        <StatDisplay id="herd-animal" name="Herd Animal" value={animalSpecies} />
                        {herdType ? (
                            <StatDisplay id="herd-type" name="Herd Type" value={herdType} />
                        ) : null}
                    </SimpleGrid>
                </ContentBox.Section>
                <ContentBox.Section>
                    <SimpleGrid columns={2}>
                        <StatDisplay
                            id="herd-location"
                            name="Herd Location"
                            value={
                                !snapshot ? undefined : (
                                    <PaddockLink paddockId={snapshot.paddockId} />
                                )
                            }
                            emptyStateMessage="No past data"
                        />
                        <StatDisplay
                            id="herd-total"
                            name="Total Animals"
                            value={!snapshot ? undefined : snapshot.totalAnimals}
                            emptyStateMessage="No past data"
                        />
                    </SimpleGrid>
                </ContentBox.Section>
                <Actions
                    sticky
                    primaryAction={{
                        text: 'Edit',
                        startIcon: 'edit',
                        onClick: () => {
                            navigate('edit');
                        },
                    }}
                    secondaryActions={[
                        {
                            text: 'Add Task',
                            startIcon: 'task',
                            onClick: () => {
                                routeActions.create('Task', { parentId: herdId });
                            },
                        },
                        {
                            text: 'Delete',
                            startIcon: 'delete',
                            theme: 'danger',
                            onClick: () => {
                                routeActions.remove(herdId, { type: 'Herd' });
                            },
                        },
                    ]}
                    backAction={{
                        text: 'All Herds',
                        onClick: () => {
                            routeActions.openList('Herd');
                        },
                    }}
                />
            </ContentBox.Section>
        </ContentBox>
    );
}

function PaddockLink({ paddockId }: { paddockId: HerdSnapshot['paddockId'] }) {
    const routeActions = useRouteActions();
    const paddock = usePaddock(paddockId);

    if (paddock?.name == null) return <>Offsite</>;

    return (
        <InlineButton
            text={paddock.name}
            onClick={() => {
                routeActions.goto(paddock.id, { type: 'Paddock' });
            }}
        />
    );
}
